/**
 * Server-sent events read from a fetch response (EventSource cannot POST):
 * `event:` and `data:` lines, blocks separated by a blank line, comments
 * (`: heartbeat`) skipped. Chunks may split anywhere, also inside a line.
 */

export interface SseEvent {
  event: string;
  data: string;
}

export class SseParser {
  private buffer = "";

  /** The events completed by this chunk. */
  feed(chunk: string): SseEvent[] {
    this.buffer += chunk.replace(/\r\n?/g, "\n");
    const out: SseEvent[] = [];
    let end = this.buffer.indexOf("\n\n");
    while (end >= 0) {
      const block = this.buffer.slice(0, end);
      this.buffer = this.buffer.slice(end + 2);
      const event = parseBlock(block);
      if (event) out.push(event);
      end = this.buffer.indexOf("\n\n");
    }
    return out;
  }

  /** A last event the stream ended without a blank line after. */
  flush(): SseEvent[] {
    const event = parseBlock(this.buffer);
    this.buffer = "";
    return event ? [event] : [];
  }
}

function parseBlock(block: string): SseEvent | null {
  let event = "message";
  const data: string[] = [];
  for (const line of block.split("\n")) {
    if (!line || line.startsWith(":")) continue;
    const colon = line.indexOf(":");
    const field = colon < 0 ? line : line.slice(0, colon);
    const value = colon < 0 ? "" : line.slice(colon + 1).replace(/^ /, "");
    if (field === "event") event = value;
    else if (field === "data") data.push(value);
  }
  return data.length ? { event, data: data.join("\n") } : null;
}

/** The events of a streamed response, as they arrive. */
export async function* readSse(response: Response): AsyncGenerator<SseEvent> {
  if (!response.body) return;
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  const parser = new SseParser();
  let finished = false;
  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      yield* parser.feed(decoder.decode(value, { stream: true }));
    }
    finished = true;
    yield* parser.feed(decoder.decode());
    yield* parser.flush();
  } finally {
    // Left early (the consumer stopped reading or failed): close the stream instead of leaving it open.
    if (!finished) await reader.cancel().catch(() => undefined);
    reader.releaseLock();
  }
}
